import apiClient from './client';

/**
 * Status enum mirroring `matching_sessions.status` (DB CHECK constraint).
 * Source: medley-api/constants/matchingSession.ts.
 */
export type MatchingSessionStatus =
  | 'submitting'
  | 'ready'
  | 'in_progress'
  | 'awaiting_finalize'
  | 'completed'
  | 'failed'
  | 'expired';

/**
 * Row item from `GET /api/cohorts/:cohortId/matching-sessions` (MED-829 / E23-8).
 * The owner_* fields are joined from the users table on the backend.
 */
export interface MatchingSessionListItem {
  id: string;
  cohort_id: string;
  external_session_id: string | null;
  opened_by_admin_id: string;
  status: MatchingSessionStatus;
  error_message: string | null;
  user_in_valid: boolean | null;
  data_as_of_at: string | null;
  received_at: string | null;
  rep_name: string | null;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  owner_first_name: string | null;
  owner_last_name: string | null;
  owner_avatar_url: string | null;
}

/** Response from `POST /api/cohorts/:cohortId/matching-sessions` (E23-4 leg A). */
export interface CreateMatchingSessionResponse {
  session_id: string;
  matching_url: string;
}

/** Response from `POST /api/matching-sessions/:id/resume` (E23-5 leg F). */
export interface ResumeMatchingSessionResponse {
  matching_url: string;
  status: MatchingSessionStatus;
}

/** A single matching-readiness blocker. */
export interface MatchingReadinessBlocker {
  code:
    | 'COHORT_NO_INTAKE_FORM'
    | 'NO_ELIGIBLE_PARTICIPANTS'
    | 'INSUFFICIENT_PARTICIPANTS_FOR_MATCHING';
  message: string;
}

/** Response from `GET /api/cohorts/:cohortId/matching-sessions/readiness`. */
export interface MatchingReadiness {
  ready: boolean;
  blocking_reasons: MatchingReadinessBlocker[];
  meta: {
    eligible_participant_count: number;
    intake_form_id: string | null;
    effective_min_group_size: number;
  };
}

export const matchingSessionsApi = {
  /**
   * Leg (A) — Start a matching session for a cohort. Always creates a new row
   * for the calling admin (1:N model). Returns the SGOS session id and the
   * matching-server URL to navigate to.
   */
  start(cohortId: string): Promise<CreateMatchingSessionResponse> {
    return apiClient.post<CreateMatchingSessionResponse>(
      `/api/cohorts/${cohortId}/matching-sessions`,
      {},
    );
  },

  /**
   * List all non-expired, non-soft-deleted matching sessions for a cohort,
   * sorted by `updated_at DESC`. Polled every 30 s by the sessions panel.
   */
  list(cohortId: string): Promise<MatchingSessionListItem[]> {
    return apiClient.get<MatchingSessionListItem[]>(
      `/api/cohorts/${cohortId}/matching-sessions`,
    );
  },

  /**
   * Leg (F) — Re-mint a navigation token for the calling admin's own row.
   * The row must be owned by the caller and in a resumable state per §7.
   */
  resume(matchingSessionId: string): Promise<ResumeMatchingSessionResponse> {
    return apiClient.post<ResumeMatchingSessionResponse>(
      `/api/matching-sessions/${matchingSessionId}/resume`,
      {},
    );
  },

  /**
   * Pre-flight readiness probe — does this cohort have the data needed to
   * start matching? Used by the Groups tab to disable the Start Matching
   * button (and surface a list of blockers) before a click that would
   * otherwise hit a 4xx error.
   */
  readiness(cohortId: string): Promise<MatchingReadiness> {
    return apiClient.get<MatchingReadiness>(
      `/api/cohorts/${cohortId}/matching-sessions/readiness`,
    );
  },
};
